import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase';
import {
  Municipality,
  MunicipalSchedule,
  MunicipalException,
  MunicipalEvent,
  MunicipalAnnouncement,
} from '@/types';

interface MunicipalContextValue {
  municipalities: Municipality[];
  allSchedules: MunicipalSchedule[];
  allExceptions: MunicipalException[];
  allEvents: MunicipalEvent[];
  allAnnouncements: MunicipalAnnouncement[];
  unreadAnnouncementCount: number;
  isLoaded: boolean;
  getUpcomingPickups: (days: number) => Array<{ schedule: MunicipalSchedule; date: string; exception?: MunicipalException }>;
}

const MunicipalContext = createContext<MunicipalContextValue>({
  municipalities: [],
  allSchedules: [],
  allExceptions: [],
  allEvents: [],
  allAnnouncements: [],
  unreadAnnouncementCount: 0,
  isLoaded: false,
  getUpcomingPickups: () => [],
});

function getNextOccurrences(schedule: MunicipalSchedule, fromDate: Date, days: number): string[] {
  const results: string[] = [];
  const { weekday, interval, biweeklyGroup, startDate } = schedule.recurrence;
  const start = new Date(startDate);
  const end = new Date(fromDate);
  end.setDate(end.getDate() + days);

  const cur = new Date(fromDate);
  cur.setHours(0, 0, 0, 0);

  while (cur <= end) {
    if (cur.getDay() === weekday) {
      if (interval === 'weekly') {
        results.push(cur.toISOString().split('T')[0]);
      } else if (interval === 'biweekly' && biweeklyGroup) {
        const daysDiff = Math.floor((cur.getTime() - start.getTime()) / 86_400_000);
        const weekNum = Math.floor(daysDiff / 7);
        const isGroupA = weekNum % 2 === 0;
        if ((biweeklyGroup === 'A' && isGroupA) || (biweeklyGroup === 'B' && !isGroupA)) {
          results.push(cur.toISOString().split('T')[0]);
        }
      }
    }
    cur.setDate(cur.getDate() + 1);
  }
  return results;
}

export function MunicipalProvider({
  children,
  subscribedIds,
}: {
  children: React.ReactNode;
  subscribedIds: string[] | undefined;
}) {
  const [municipalities, setMunicipalities] = useState<Municipality[]>([]);
  const [allSchedules, setAllSchedules] = useState<MunicipalSchedule[]>([]);
  const [allExceptions, setAllExceptions] = useState<MunicipalException[]>([]);
  const [allEvents, setAllEvents] = useState<MunicipalEvent[]>([]);
  const [allAnnouncements, setAllAnnouncements] = useState<MunicipalAnnouncement[]>([]);
  const [isLoaded, setIsLoaded] = useState(!subscribedIds?.length);

  const unsubs = useRef<Array<() => void>>([]);

  useEffect(() => {
    unsubs.current.forEach(u => u());
    unsubs.current = [];

    if (!subscribedIds?.length) {
      setMunicipalities([]);
      setAllSchedules([]);
      setAllExceptions([]);
      setAllEvents([]);
      setAllAnnouncements([]);
      setIsLoaded(true);
      return;
    }

    setIsLoaded(false);
    let loaded = 0;
    // 1 shared municipalities listener + 4 subcollection listeners per subscribed municipality
    const total = 1 + subscribedIds.length * 4;
    const markLoaded = () => { loaded++; if (loaded >= total) setIsLoaded(true); };

    const tempSchedules: Record<string, MunicipalSchedule[]> = {};
    const tempExceptions: Record<string, MunicipalException[]> = {};
    const tempEvents: Record<string, MunicipalEvent[]> = {};
    const tempAnnouncements: Record<string, MunicipalAnnouncement[]> = {};

    // Single municipalities collection listener shared across all subscriptions
    unsubs.current.push(onSnapshot(
      collection(db, 'municipalities'),
      snap => {
        setMunicipalities(snap.docs
          .filter(d => subscribedIds.includes(d.id))
          .map(d => ({ id: d.id, ...d.data() } as Municipality))
        );
        markLoaded();
      },
      () => markLoaded()
    ));

    for (const munId of subscribedIds) {
      const sub = <T,>(col: string, setter: (id: string, v: T[]) => void) => {
        unsubs.current.push(onSnapshot(
          collection(db, 'municipalities', munId, col),
          snap => {
            setter(munId, snap.docs.map(d => ({ id: d.id, ...d.data() } as T)));
            markLoaded();
          },
          () => markLoaded()
        ));
      };

      sub<MunicipalSchedule>('schedules', (id, v) => {
        tempSchedules[id] = v;
        setAllSchedules(Object.values(tempSchedules).flat());
      });
      sub<MunicipalException>('exceptions', (id, v) => {
        tempExceptions[id] = v;
        setAllExceptions(Object.values(tempExceptions).flat());
      });
      sub<MunicipalEvent>('events', (id, v) => {
        tempEvents[id] = v;
        setAllEvents(Object.values(tempEvents).flat());
      });
      sub<MunicipalAnnouncement>('announcements', (id, v) => {
        tempAnnouncements[id] = v;
        setAllAnnouncements(Object.values(tempAnnouncements).flat());
      });
    }

    return () => { unsubs.current.forEach(u => u()); unsubs.current = []; };
  }, [JSON.stringify(subscribedIds)]);

  const now = new Date();
  const unreadAnnouncementCount = allAnnouncements.filter(a =>
    a.status === 'published' &&
    (!a.expiresAt || new Date(a.expiresAt) > now)
  ).length;

  const getUpcomingPickups = (days: number) => {
    const results: Array<{ schedule: MunicipalSchedule; date: string; exception?: MunicipalException }> = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (const schedule of allSchedules.filter(s => s.isActive)) {
      const dates = getNextOccurrences(schedule, today, days);
      for (const dateStr of dates) {
        const exception = allExceptions.find(e => e.scheduleId === schedule.id && e.originalDate === dateStr);
        if (!exception || exception.newDate !== null) {
          results.push({ schedule, date: exception?.newDate ?? dateStr, exception });
        }
      }
    }

    return results.sort((a, b) => a.date.localeCompare(b.date));
  };

  return (
    <MunicipalContext.Provider value={{
      municipalities,
      allSchedules,
      allExceptions,
      allEvents,
      allAnnouncements,
      unreadAnnouncementCount,
      isLoaded,
      getUpcomingPickups,
    }}>
      {children}
    </MunicipalContext.Provider>
  );
}

export function useMunicipal() {
  return useContext(MunicipalContext);
}
